// src/components/CameraStreamHLSIntegrated.jsx
// ✅ COMPLETE VERSION - Handles both live HLS and playback video

import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import Hls from "hls.js";
import { FiAlertTriangle, FiZap, FiVideo, FiMaximize, FiClock } from "react-icons/fi";

const MIN_WIDTH_PCT = 0.45;

export default function CameraStreamHLSIntegrated({
  camId,
  mtxPath,
  baseUrl = "https://vision.viewmax.net",
  wsBaseUrl = "wss://vision.viewmax.net",
  muted = true,
  isLive = false,
  
  // ✅ NEW: Playback props
  playbackSource = null, // { type: 'file', fileUrl, seekSeconds, _clipStartTime }
  
  date,
  timezone = "America/Vancouver",
  fullRecordings = [],
  detections: initialDetections = [],
  playbackDate = null,
  onError,
  onTimeUpdate,
  onDetectionClick,
  onTimelineSeek,
}) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const containerRef = useRef(null);
  const progressBarRef = useRef(null);
  const wsRef = useRef(null);

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mediaTime, setMediaTime] = useState(0);
  const [mediaDuration, setMediaDuration] = useState(0);
  const [hoverTime, setHoverTime] = useState(null);
  const [hoverDetection, setHoverDetection] = useState(null);
  const [detections, setDetections] = useState(initialDetections);
  
  const [reconnectAttempt, setReconnectAttempt] = useState(0);
  const MAX_RECONNECT_ATTEMPTS = 5;

  const hlsUrl = `${baseUrl}/hls/${mtxPath || `cam${camId}`}/index.m3u8`;

  const baseStart = useMemo(() => new Date(`${date}T00:00:00`), [date]);
  const baseEnd = useMemo(() => new Date(`${date}T23:59:59`), [date]);
  const dayMs = useMemo(
    () => Math.max(baseEnd.getTime() - baseStart.getTime(), 1),
    [baseStart, baseEnd]
  );

  useEffect(() => {
    setDetections(Array.isArray(initialDetections) ? initialDetections : []);
  }, [initialDetections]);

  // WebSocket for live detections
  useEffect(() => {
    if (!isLive) return;
    if (!wsBaseUrl || !camId) return;

    const wsUrl = `${wsBaseUrl}/ws/camera/${camId}/analytics/`;
    console.log("🔌 Connecting to WebSocket:", wsUrl);

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => console.log("✅ WebSocket connected");

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        const newDetection = {
          ...data,
          isRecent: true,
          id: data.id || `${Date.now()}_${Math.random()}`,
        };

        setDetections((prev) => [...prev, newDetection]);

        setTimeout(() => {
          setDetections((prev) =>
            prev.map((d) =>
              d.id === newDetection.id ? { ...d, isRecent: false } : d
            )
          );
        }, 10 * 60 * 1000);
      } catch (err) {
        console.error("❌ WebSocket parse error:", err);
      }
    };

    ws.onerror = (err) => console.error("❌ WebSocket error:", err);
    ws.onclose = () => console.log("🔌 WebSocket disconnected");

    return () => {
      try {
        if (wsRef.current && wsRef.current.readyState <= 1) {
          wsRef.current.close();
        }
      } catch {}
      wsRef.current = null;
    };
  }, [isLive, camId, wsBaseUrl]);

  const dateToPercent = useCallback(
    (d) => {
      if (!d) return null;
      const t = new Date(d).getTime();
      if (Number.isNaN(t)) return null;

      const clamped = Math.min(
        Math.max(t, baseStart.getTime()),
        baseEnd.getTime()
      );
      return ((clamped - baseStart.getTime()) / dayMs) * 100;
    },
    [baseStart, baseEnd, dayMs]
  );

  const percentToDate = useCallback(
    (percent) => {
      const p = Math.min(Math.max(percent, 0), 100);
      const targetMs = baseStart.getTime() + (p / 100) * dayMs;
      return new Date(targetMs);
    },
    [baseStart, dayMs]
  );

  const coverageBars = useMemo(() => {
    return (fullRecordings || [])
      .map((rec) => {
        if (!rec?.start_time) return null;

        const start = dateToPercent(rec.start_time);
        if (start == null) return null;

        const durationSec = rec.duration_s || 300;
        const endTime = new Date(
          new Date(rec.start_time).getTime() + durationSec * 1000
        );

        const end = dateToPercent(endTime);
        const width = Math.max((end != null ? end - start : 0), MIN_WIDTH_PCT);

        return { start, width, rec };
      })
      .filter(Boolean);
  }, [fullRecordings, dateToPercent]);

  const detectionMarkers = useMemo(() => {
    return (detections || [])
      .map((d) => {
        const ts = d.timestamp || d.start_time || d.timestamp_utc;
        const left = dateToPercent(ts);
        if (left == null) return null;

        return {
          raw: d,
          left: Math.min(Math.max(left, 0.1), 99.9),
          isRecent: !!d.isRecent,
        };
      })
      .filter(Boolean);
  }, [detections, dateToPercent]);

  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => {
    if (!isLive) return;
    const id = setInterval(() => setNowTick(Date.now()), 15000);
    return () => clearInterval(id);
  }, [isLive]);

  const currentPercent = useMemo(() => {
    if (isLive) {
      return dateToPercent(new Date(nowTick));
    }
    return null;
  }, [isLive, nowTick, dateToPercent]);

  // ✅ ENHANCED: Video initialization - handles BOTH live HLS and playback
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    setLoading(true);
    setError(null);

    // Clean up any existing HLS instance
    if (hlsRef.current) {
      try {
        hlsRef.current.destroy();
      } catch {}
      hlsRef.current = null;
    }

    // Reset video
    try {
      video.pause();
    } catch {}
    video.removeAttribute("src");
    video.load();

    // ✅ PLAYBACK MODE - Load recorded video file
    if (!isLive && playbackSource?.fileUrl) {
      console.log("🎬 Loading playback video:", playbackSource.fileUrl);
      
      video.src = playbackSource.fileUrl;
      
      video.onloadedmetadata = () => {
        setLoading(false);
        setMediaDuration(video.duration || 0);
        
        // ✅ Seek to specified position
        if (playbackSource.seekSeconds) {
          try {
            video.currentTime = playbackSource.seekSeconds;
          } catch (e) {
            console.warn("Seek failed", e);
          }
        }
        
        video.play().catch(() => console.warn("Autoplay blocked"));
      };

      video.onerror = () => {
        console.error("❌ Playback video error");
        setError("Playback unavailable");
        setLoading(false);
      };

      return () => {
        video.onloadedmetadata = null;
        video.onerror = null;
      };
    }

    // ✅ LIVE MODE - Load HLS stream
    if (isLive) {
      console.log("🎬 Initializing HLS live stream:", hlsUrl);

      const handleNativeError = () => {
        console.error("❌ Native HLS video error");
        setError("Stream unavailable");
        setLoading(false);
        if (onError) onError(new Error("Stream error"));
      };

      if (video.canPlayType("application/vnd.apple.mpegurl")) {
        console.log("✅ Using native HLS");
        video.src = hlsUrl;

        video.onloadedmetadata = () => {
          setLoading(false);
          setMediaDuration(video.duration || 0);
          setReconnectAttempt(0);
        };

        video.onerror = handleNativeError;

        video
          .play()
          .then(() => {})
          .catch(() => {});
      } else if (Hls.isSupported()) {
        console.log("✅ Using HLS.js");

        const hls = new Hls({
          debug: false,
          enableWorker: true,
          lowLatencyMode: true,
          backBufferLength: 10,
          maxBufferLength: 10,
          maxMaxBufferLength: 20,
        });

        hls.loadSource(hlsUrl);
        hls.attachMedia(video);

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          console.log("✅ HLS manifest parsed");
          setLoading(false);
          setReconnectAttempt(0);
          video
            .play()
            .then(() => {})
            .catch(() => {});
        });

        hls.on(Hls.Events.ERROR, (event, data) => {
          console.error("❌ HLS error:", data);

          if (!data?.fatal) return;

          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              if (reconnectAttempt < MAX_RECONNECT_ATTEMPTS) {
                const delay = Math.min(1000 * Math.pow(2, reconnectAttempt), 30000);
                console.log(`⚠️ Network error, reconnecting in ${delay}ms (attempt ${reconnectAttempt + 1}/${MAX_RECONNECT_ATTEMPTS})`);
                
                setError(`Reconnecting... (${reconnectAttempt + 1}/${MAX_RECONNECT_ATTEMPTS})`);
                
                setTimeout(() => {
                  console.log('🔄 Attempting reconnect...');
                  hls.loadSource(hlsUrl);
                  setReconnectAttempt(prev => prev + 1);
                }, delay);
              } else {
                setError("Network error — camera may be offline");
                setLoading(false);
                if (onError) onError(new Error("Network error"));
              }
              break;

            case Hls.ErrorTypes.MEDIA_ERROR:
              console.warn("⚠️ Media error, trying recover...");
              try {
                hls.recoverMediaError();
              } catch {
                setError("Media error — unable to recover");
                setLoading(false);
                if (onError) onError(new Error("Media error"));
              }
              break;

            default:
              try {
                hls.destroy();
              } catch {}
              setError("Stream unavailable");
              setLoading(false);
              if (onError) onError(new Error("Fatal error"));
              break;
          }
        });

        hlsRef.current = hls;
      } else {
        setError("HLS not supported in this browser");
        setLoading(false);
      }

      return () => {
        try {
          video.onloadedmetadata = null;
          video.onerror = null;
        } catch {}
        if (hlsRef.current) {
          try {
            hlsRef.current.destroy();
          } catch {}
          hlsRef.current = null;
        }
      };
    }
  }, [isLive, hlsUrl, playbackSource, onError, reconnectAttempt]);

  // ✅ Track playback time and update playbackDate
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const updateTime = () => {
      const t = video.currentTime || 0;
      setMediaTime(t);
      setMediaDuration(video.duration || 0);
      if (onTimeUpdate) onTimeUpdate(t);
    };

    video.addEventListener("timeupdate", updateTime);
    video.addEventListener("loadedmetadata", updateTime);

    return () => {
      video.removeEventListener("timeupdate", updateTime);
      video.removeEventListener("loadedmetadata", updateTime);
    };
  }, [onTimeUpdate]);

  const handleTimelineClick = useCallback(
    (e) => {
      if (!progressBarRef.current) return;

      const rect = progressBarRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percent = (x / rect.width) * 100;

      const timestamp = percentToDate(percent);

      if (onTimelineSeek) onTimelineSeek(timestamp);
    },
    [percentToDate, onTimelineSeek]
  );

  const handleTimelineHover = useCallback(
    (e) => {
      if (!progressBarRef.current) return;

      const rect = progressBarRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percent = (x / rect.width) * 100;

      const timestamp = percentToDate(percent);
      setHoverTime(timestamp);

      const detection = detectionMarkers.find((det) => {
        const detTime = percentToDate(det.left);
        return Math.abs(detTime.getTime() - timestamp.getTime()) < 5 * 60 * 1000;
      });

      setHoverDetection(detection?.raw || null);
    },
    [percentToDate, detectionMarkers]
  );

  const handleTimelineLeave = useCallback(() => {
    setHoverTime(null);
    setHoverDetection(null);
  }, []);

  const toggleFullscreen = useCallback(() => {
    const container = containerRef.current;
    const video = videoRef.current;
    
    if (!container || !video) return;

    const isFullscreen = 
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement;

    if (isFullscreen) {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    } else {
      const element = container;
      
      if (element.requestFullscreen) {
        element.requestFullscreen();
      } else if (element.webkitRequestFullscreen) {
        element.webkitRequestFullscreen();
      } else if (element.webkitEnterFullscreen) {
        element.webkitEnterFullscreen();
      } else if (element.mozRequestFullScreen) {
        element.mozRequestFullScreen();
      } else if (element.msRequestFullscreen) {
        element.msRequestFullscreen();
      } else if (video.webkitEnterFullscreen) {
        video.webkitEnterFullscreen();
      }
    }
  }, []);

  const formatTime = (d) => {
    if (!d) return "--:--";
    try {
      return d.toLocaleTimeString("en-CA", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        timeZone: timezone,
      });
    } catch {
      return d.toLocaleTimeString("en-CA", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    }
  };

  if (error && !error.includes("Reconnecting")) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-black">
        <div className="text-center text-gray-400 px-6">
          <FiAlertTriangle className="w-10 h-10 mx-auto mb-3 text-red-500" />
          <p className="text-sm font-medium">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative w-full h-full bg-black">
      {/* Video */}
      <div className="absolute inset-0 pb-32 bg-black">
        <video
          ref={videoRef}
          autoPlay
          muted={muted}
          playsInline
          controls={!isLive} // ✅ Show controls in playback mode
          className="w-full h-full object-cover md:object-contain"
        />

        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-20">
            <div className="text-white text-sm">Loading...</div>
          </div>
        )}


{/* ✅ Playback time display - always visible during playback */}
{!isLive && playbackDate && (
  <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-green-500/90 backdrop-blur-sm text-white px-4 py-2 rounded-lg shadow-lg z-10 flex items-center gap-2">
    <FiClock className="w-4 h-4" />
    <span className="font-bold text-sm">{formatTime(playbackDate)}</span>
  </div>
)}




        {/* ✅ Reconnecting indicator */}
        {error && error.includes("Reconnecting") && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-20">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="text-white text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Live indicator */}

        {/* ✅ Playback indicator */}
        {!isLive && playbackSource?.isRecentFallback && (
          <div className="absolute top-3 left-3 bg-orange-500/20 border border-orange-500/60 text-orange-300 px-3 py-1 text-xs rounded flex items-center gap-1.5 z-10">
            <FiClock className="w-3 h-3 animate-pulse" />
            <span>Recent detection (clip)</span>
          </div>
        )}

        {/* Fullscreen button */}
        <button
          onClick={toggleFullscreen}
          className="absolute top-3 right-3 bg-black/60 hover:bg-black/80 text-white p-2 rounded z-10 transition-colors"
        >
          <FiMaximize className="w-5 h-5" />
        </button>
      </div>

      {/* Integrated Timeline */}
      <div className="absolute bottom-0 left-0 right-0 bg-gray-900/90 backdrop-blur-sm p-2 space-y-1">
        {/* Labels */}

{/* Labels */}
<div className="flex justify-between text-xs text-gray-400">
  <span className="flex items-center gap-1">
    <FiVideo className="w-3 h-3" />
    {date}
  </span>
  {/* ✅ Show playback time prominently when in playback mode */}
  {!isLive && playbackDate ? (
    <span className="text-sm font-bold text-green-400">
      {formatTime(playbackDate)}
    </span>
  ) : (
    <span>{hoverTime ? formatTime(hoverTime) : formatTime(new Date())}</span>
  )}
</div>


        {/* Progress bar */}
        <div
          ref={progressBarRef}
          className="relative h-12 bg-gray-800 rounded-lg cursor-pointer overflow-hidden"
          onClick={handleTimelineClick}
          onMouseMove={handleTimelineHover}
          onMouseLeave={handleTimelineLeave}
        >
          {/* Hour grid */}
          <div className="absolute inset-0 flex z-0">
            {Array.from({ length: 24 }).map((_, i) => (
              <div
                key={i}
                className="flex-1 border-l border-gray-700/60 first:border-l-0"
              />
            ))}
          </div>

          {/* Recording segments */}
          <div className="absolute inset-0 z-10">
            {coverageBars.map((bar, i) => (
              <div
                key={i}
                className="absolute top-0.5 bottom-0.5 rounded-sm bg-blue-500/40 hover:bg-blue-400/60 transition-colors"
                style={{
                  left: `${bar.start}%`,
                  width: `${bar.width}%`,
                }}
                title={`Recording: ${formatTime(new Date(bar.rec.start_time))}`}
              />
            ))}
          </div>

          {/* Detection markers */}
          <div className="absolute inset-0 z-20 pointer-events-none">
            {detectionMarkers.map((dot, i) => (
              <button
                key={dot.raw?.id || i}
                className="absolute top-0 bottom-0 flex items-center justify-center pointer-events-auto"
                style={{ left: `${dot.left}%` }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onDetectionClick) onDetectionClick(dot.raw);
                }}
              >
                <div
                  className={`w-2 h-2 rounded-full ${
                    dot.isRecent ? "bg-orange-500 animate-pulse" : "bg-red-500"
                  }`}
                  title={dot.raw?.label || "Motion detected"}
                />
              </button>
            ))}
          </div>

          {/* Current marker (LIVE only) */}
          {currentPercent != null && (
            <div
              className="absolute top-0 bottom-0 w-1 bg-blue-500 z-30"
              style={{ left: `${currentPercent}%` }}
            >
              <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-3 h-3 bg-blue-500 rounded-full" />
            </div>
          )}

          {/* ✅ Playback position marker */}
{/* ✅ Playback position marker - BIGGER and more visible */}
{playbackDate && !isLive && (
  <div
    className="absolute top-0 bottom-0 w-1.5 bg-green-500 z-30 shadow-lg"
    style={{ left: `${dateToPercent(playbackDate)}%` }}
  >
    {/* Bigger circle at top */}
    <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-4 h-4 bg-green-500 rounded-full border-2 border-white shadow-lg" />
    {/* Time label above marker */}
    <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-green-500 text-white text-[10px] px-2 py-0.5 rounded whitespace-nowrap font-bold">
      {formatTime(playbackDate)}
    </div>
  </div>
)}

          {/* Hover indicator */}
{/* Hover indicator - less flickering */}
{hoverTime && !playbackDate && (
  <div
    className="absolute top-0 bottom-0 w-px bg-white/50 z-30 transition-opacity duration-150"
    style={{ left: `${dateToPercent(hoverTime)}%` }}
  />
)}

        </div>

        {/* Hover tooltip */}
        {hoverTime && (
          <div className="text-xs text-gray-300 flex items-center gap-3">
            <span>{formatTime(hoverTime)}</span>
            {hoverDetection && (
              <span className="flex items-center gap-1 text-red-400">
                <FiZap className="w-3 h-3" />
                {hoverDetection.label || "Motion detected"}
                {hoverDetection.confidence != null && (
                  <span className="text-gray-400">
                    ({Math.round(hoverDetection.confidence * 100)}%)
                  </span>
                )}
              </span>
            )}
          </div>
        )}

        {/* Hour markers */}
        <div className="flex justify-between text-xs text-gray-500">
          <span>00:00</span>
          <span>06:00</span>
          <span>12:00</span>
          <span>18:00</span>
          <span>23:59</span>
        </div>

        {/* Stats */}
        <div className="flex justify-between text-xs text-gray-400 pt-1 border-t border-gray-800">
          <span className="flex items-center gap-2">
            <FiVideo className="w-3 h-3" />
            {fullRecordings.length} recordings
          </span>
          <span className="flex items-center gap-2">
            <FiZap className="w-3 h-3" />
            {detections.length} detections
          </span>
        </div>
      </div>
    </div>
  );
}
